import type { ProjectModelView } from '@actschurch/ccms-sdk';

export function parseBackendDate(dt: string | number | null | undefined): Date | null {
  if (dt == null) return null;
  if (typeof dt === 'number') {
    const str = String(dt);
    if (str.length >= 8) {
      const year = parseInt(str.substring(0, 4));
      const month = parseInt(str.substring(4, 6)) - 1;
      const day = parseInt(str.substring(6, 8));
      const hours = str.length >= 10 ? parseInt(str.substring(8, 10)) : 0;
      const mins = str.length >= 12 ? parseInt(str.substring(10, 12)) : 0;
      return new Date(year, month, day, hours, mins);
    }
    return null;
  }
  const d = new Date(dt);
  // .NET serialises an unset DateTime as 0001-01-01 — treat it as "no date", not a date
  // two thousand years overdue.
  return Number.isNaN(d.getTime()) || d.getFullYear() <= 1 ? null : d;
}

export function formatDate(dt: string | number | null | undefined, fallback = '—'): string {
  const d = parseBackendDate(dt);
  if (!d) return fallback;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

/**
 * Rich-text (HTML) field -> plain text for list/card previews. Descriptions are authored
 * in the HTML editor, so rendering them inside <Text> shows raw `<p>` markup.
 * DOMParser builds an inert document: unlike assigning innerHTML to an element, it never
 * loads images or fires `onerror` handlers from stored content.
 */
export function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return '';
  const withBreaks = html
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n');
  const text = new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent ?? '';
  return text.replace(/ /g, ' ').replace(/\n{3,}/g, '\n\n').trim();
}

export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return 'R 0';
  return `R ${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function computeProgressPercentage(p: ProjectModelView): number {
  if (typeof p.progressPercentage === 'number') return Math.round(p.progressPercentage);
  if (!p.totalTasks) return 0;
  return Math.round(((p.completedTasks || 0) / p.totalTasks) * 100);
}

export function computeBudgetUtilization(p: ProjectModelView): number {
  if (typeof p.budgetUtilizationPercentage === 'number') {
    return Math.round(p.budgetUtilizationPercentage);
  }
  if (!p.budgetTotal) return 0;
  return Math.round((p.budgetSpent / p.budgetTotal) * 100);
}

export function computeMilestoneProgress(p: ProjectModelView): number {
  if (typeof p.milestoneProgressPercentage === 'number') {
    return Math.round(p.milestoneProgressPercentage);
  }
  if (!p.totalMilestones) return 0;
  return Math.round((p.completedMilestones / p.totalMilestones) * 100);
}

export function computeHealthStatus(p: ProjectModelView): string {
  if (p.healthStatus) return p.healthStatus;
  const progress = computeProgressPercentage(p);
  if (progress >= 100) return 'Completed';
  if (p.isOverDue || p.highRiskCount > 0) return 'Critical';
  if (progress < 40 && (p.daysUntilEnd ?? 999) < 14) return 'At Risk';
  return 'On Track';
}

export function isProjectOverdue(p: ProjectModelView): boolean {
  if (p.isOverDue !== undefined) return !!p.isOverDue;
  if (!p.endDate) return false;
  const end = parseBackendDate(p.endDate);
  if (!end) return false;
  return end.getTime() < Date.now();
}

export function getOwnerDisplayName(p: ProjectModelView): string {
  if (p.ownerFullName) return p.ownerFullName;
  const first = p.ownerFirstName || '';
  const last = p.ownerSurname || '';
  const joined = `${first} ${last}`.trim();
  // An owner whose profile didn't resolve is still an owner — don't call it unassigned.
  return joined || p.ownerEmail || (p.owner ? 'Unknown user' : 'Unassigned');
}

export function sortProjectsByModifiedDesc(projects: ProjectModelView[]): ProjectModelView[] {
  return [...projects].sort((a, b) => {
    const aDate = parseBackendDate(a.modifiedDateTime || a.createdDateTime)?.getTime() ?? 0;
    const bDate = parseBackendDate(b.modifiedDateTime || b.createdDateTime)?.getTime() ?? 0;
    return bDate - aDate;
  });
}
